import { Location } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork } from 'app/core/core.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ProjectDialogComponent } from '../project-dialog/project-dialog.component';
import { Utility } from 'app/core/utility';
import { Attachment } from 'app/core/model/attachment';
import { FileService } from 'app/core/file.service';
import { Customer } from 'app/core/model/customer';
import { CustomerMixStrengthLink } from 'app/core/model/customer-mix-strength-link';
import { CustomerProductPriceOverride } from 'app/core/model/customer-product-price-override';
import { MixStrength } from 'app/core/model/mix-strength';
import { CustomerMixStrengthDialogComponent } from '../customer-mix-strength-dialog/customer-mix-strength-dialog.component';
import { CustomerProductPriceOverrideDialogComponent } from '../customer-product-price-override-dialog/customer-product-price-override-dialog.component';
import { CustomerTaxCode, Invoice, InvoicePaymentMethod, InvoiceStatus, Product } from 'app/core/model/entity-model';
import _ from 'lodash';
import { InputDialogComponent } from 'app/shared/shared.module';

@Component({
    standalone: false,
    selector: 'app-customer-detail',
    templateUrl: './customer-detail.component.html',
    styleUrls: ['./customer-detail.component.scss'],
})
export class CustomerDetailComponent implements OnInit {
    key: number | string;
    customer: Customer;
    customerMixStrengths: CustomerMixStrengthLink[] = [];
    customerProductPriceOverrides: CustomerProductPriceOverride[] = [];
    mixStrengths: MixStrength[];
    products: Product[];
    taxCodes: CustomerTaxCode[];
    customerTypes: any[];
    projects: any[];
    invoices: Invoice[];
    invoiceStatuses: InvoiceStatus[];
    states: string[];
    countries: string[];
    communicationOptions: string[] = ['Mail', 'Email', 'Both'];
    terms: any[];
    unixKeys: string[];
    paymentMethods: InvoicePaymentMethod[];
    limestoneCharge?: number;

    uploadUrl: string;
    uploadHeader: any;
    attachments: Attachment[];

    canEdit: boolean;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: BrewersUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private appInfo: AppInformationService,
        private auth: AuthService,
        private modalService: BsModalService,
        private fileService: FileService
    ) {}

    canDeactivate() {
        return !this.hasChanges();
    }

    get hasActiveOverrides() {
        return this.customerProductPriceOverrides?.some((override) => override.isActive) ?? false;
    }

    async ngOnInit() {
        this.canEdit = this.auth.hasRole('Customers:Admin') || this.auth.hasRole('Admin');
        this.terms = Utility.Terms;
        this.customerTypes = this.uow.lookups.customerTypes as any[];
        this.states = this.uow.lookups.states;
        this.countries = this.uow.lookups.countries;
        this.invoiceStatuses = await this.uow.invoiceStatuses.whereActive('displayOrder, name');
        this.paymentMethods = await this.uow.invoicePaymentMethods.whereActive('displayOrder, name');
        this.mixStrengths = await this.uow.mixStrengths.whereActive('displayOrder, name');
        this.taxCodes = await this.uow.customerTaxCodes.whereActive();
        this.products = await this.uow.products.whereActive('description');

        this.key = this.route.snapshot.params['id'];
        if (this.key === 'new') {
            this.customer = this.uow.customers.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
            });
        } else {
            await this.getData();
            await this.getAttachments();
        }
        this.unixKeys = await this.uow.getCustomerUnixKeys(this.customer.id);
        this.uploadUrl = this.fileService.getUploadUrl(this.customer.id, FileService.Mode.Customer);
        this.uploadHeader = this.fileService.getUploadHeader();
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    customizeColumns(columns) {
        columns.forEach((column) => {
            if (column.dataField !== 'price') {
                column.allowEditing = false;
            }
        });
    }
    async onRowUpdated(e) {
        await this.uow.commit();
    }

    async getData() {
        this.customer = await this.uow.customers.byId(this.key);
        await this.getCustomerMixStrengths();
        await this.getCustomerProductPriceOverrides();
        await this.getInvoices();
        // this.projects = this.customer.projects.filter((x: any) => {
        //     if (!x.deleted) return x; else return null;
        // });
    }

    async getInvoices() {
        this.invoices = await this.uow.customers.getInvoices(this.customer.id);
    }

    async getCustomerMixStrengths() {
        this.customerMixStrengths = await this.uow.getCustomerMixStrengthLinks(this.customer.id);
    }

    async getCustomerProductPriceOverrides() {
        this.customerProductPriceOverrides = await this.uow.getCustomerProductPriceOverrides(this.customer.id);
    }

    async addProject(project?: any) {
        const modalRef = this.modalService.show(ProjectDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                entity: !!project ? project : null,
                mode: !!project ? 'edit' : 'add',
                customer: this.customer,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result && result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }
    async deleteProject() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const result = await confirm('Are you sure you want to delete the selected project?', this.appInfo.settings.company);
        if (!result) return;

        selectedRows.forEach((row) => {
            row.deleted = new Date();
            row.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit();
        await this.getData();
    }

    async save() {
        if (!this.valid()) {
            await alert('Please fix errors before saving', this.appInfo.settings.company);
            return;
        }

        if (!this.customer.companyName) {
            const success = await confirm('Company Name is blank for a Commercial customer. Do you want to continue?', this.appInfo.settings.company);
            if (!success) return;
        }

        this.appInfo.pleaseWaitShow();
        this.customer.quickBooksLastExport = null;
        await this.uow.commit();

        if (this.key === 'new') {
            this.key = this.customer.id;
            this.router.navigate([`../${this.customer.id}`], { relativeTo: this.route, replaceUrl: true });
            this.uploadUrl = this.fileService.getUploadUrl(this.customer.id, FileService.Mode.Customer);
        }
        this.appInfo.pleaseWaitHide();
    }

    async cancel() {
        const result = await confirm('Are you sure you want to cancel changes?', this.appInfo.settings.company);
        if (!result) return false;

        this.uow.rollback();
        if (!this.key || this.key === 'new') {
            this.close();
            return false;
        }
        return false;
    }

    close() {
        try {
            this.location.back();
        } catch {
            this.router.navigate(['../'], { relativeTo: this.route });
        }
    }

    valid() {
        const groupConfig = validationEngine.getGroupConfig('customerValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('customerValidationGroup');
            return result.isValid;
        } else return false;
    }

    isUnique = (e) => {
        return !this.unixKeys.some((k) => k?.toLowerCase().trim() === e.value?.toLowerCase().trim());
    };

    async getAttachments() {
        this.attachments = await this.uow.getAttachments(FileService.Mode.Customer, this.customer.id);
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        var response = this.fileService.parseUploadResponse(e);
        if (!response.success) alert(`Unable to upload the file(s). ${response.message}`, 'Upload Error');
        else await this.getAttachments();
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    openAttachment(attachment: Attachment) {
        if (!attachment) return;
        window.open(attachment.filename, '_blank');
    }

    async removeAttachment(attachment: Attachment) {
        if (!attachment) return;

        const success = await confirm('Are you sure you want to delete this attachment?', this.appInfo.settings.company);
        if (!success) return;

        await this.appInfo.pleaseWaitShow();
        attachment.deleted = new Date();
        attachment.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit([attachment]);
        await this.getAttachments();
        await this.appInfo.pleaseWaitHide();
    }

    async generateMixStrengths() {
        const mixStrengths = this.getMixStrengths();

        if (mixStrengths.length === 0) {
            await alert('All mix strengths have already been added', this.appInfo.settings.company);
            return;
        }

        const result = await confirm(`Are you sure you want to generate all remaining mix strengths?`, this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const customerMixStrengths = [];
        const date = new Date();
        mixStrengths.forEach((mixStrength) => {
            customerMixStrengths.push(
                this.uow.customerMixStrengthLinks.createEntity({
                    customerId: this.customer.id,
                    mixStrengthId: mixStrength.id,
                    price: 0,
                    created: date,
                    createdBy: this.auth.currentUser.userName,
                })
            );
        });
        await this.uow.commit(customerMixStrengths);
        this.customerMixStrengths = _([...this.customerMixStrengths, ...customerMixStrengths])
            .orderBy((cms) => cms.mixStrength.displayOrder)
            .valueOf();
        this.appInfo.pleaseWaitHide();
    }

    async mixStrengthRowDoubleClick(e: any) {
        await this.editMixStrength(e.data);
    }

    async editMixStrength(customerMixStrength?: CustomerMixStrengthLink) {
        const mixStrengths = this.getMixStrengths(customerMixStrength?.mixStrengthId);
        if (mixStrengths.length === 0) {
            await alert('All mix strengths already have been added', this.appInfo.settings.company);
            return;
        }

        const modalRef = this.modalService.show(CustomerMixStrengthDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                customerMixStrength: customerMixStrength,
                customerId: this.customer.id,
                mixStrengths: mixStrengths,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && !this.customerMixStrengths.includes(result.value)) {
                this.customerMixStrengths = _([...this.customerMixStrengths, result.value])
                    .orderBy((cms) => cms.mixStrength.displayOrder)
                    .valueOf();
            }
            modalRef.hide();
        });
    }

    async deleteMixStrength(customerMixStrengths: CustomerMixStrengthLink[]) {
        if (!customerMixStrengths || customerMixStrengths.length === 0) return;

        const result = await confirm(`Are you sure you want to delete the selected mix strength${customerMixStrengths.length > 1 ? 's' : ''}?`, this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        customerMixStrengths.forEach((customerMixStrength) => {
            customerMixStrength.deleted = new Date();
            customerMixStrength.deletedBy = this.auth.currentUser.userName;
        });
        this.customerMixStrengths = this.customerMixStrengths.filter((customerMixStrength) => !customerMixStrength.deleted);
        await this.uow.commit(customerMixStrengths);
        this.appInfo.pleaseWaitHide();
    }

    async increaseRediMixPricing() {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Increase Redi-Mix Pricing',
                dialogPrompt: 'Increase All Redi-Mix Prices',
                dialogValue: 0,
                mode: 'text',
                checkNumeric: true,
                minValue: 0,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            this.appInfo.pleaseWaitShow();
            if (result.success && result.value) {
                this.customerMixStrengths.forEach((mixStrength) => {
                    mixStrength.price += Number(result.value);
                });
                await this.uow.commit(this.customerMixStrengths);
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async applyLimestoneCharge() {
        if (this.limestoneCharge == null) {
            await alert('A limestone charge is required', this.appInfo.settings.company);
            return;
        }

        this.appInfo.pleaseWaitShow();
        const limestoneMixStrengths = this.customerMixStrengths.filter((customerMixStrength) => customerMixStrength.isLimestone);
        limestoneMixStrengths.forEach((limestoneMixStrength) => {
            const baseMixStrength = this.customerMixStrengths.find(
                (customerMixStrength) => `${customerMixStrength.trimmedMixStrengthName}+ls` === limestoneMixStrength.trimmedMixStrengthName
            );
            limestoneMixStrength.price = (baseMixStrength?.price ?? 0) + this.limestoneCharge;
        });
        await this.uow.commit(limestoneMixStrengths);
        this.limestoneCharge = null;
        this.appInfo.pleaseWaitHide();
    }

    async copyMixStrengths(customerMixStrengths: CustomerMixStrengthLink[]) {
        if (!customerMixStrengths || customerMixStrengths.length === 0) return;

        const lookupCustomers = (await this.uow.customers.getLookupCustomers()).filter((customer) => customer.id !== this.customer.id);

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Copy To Customer',
                dialogPrompt: 'Select the customer to copy mix strength pricing to',
                dialogValueArray: lookupCustomers,
                mode: 'selectWithId',
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && result.value) {
                const destinationCustomerId = result.value;
                const destinationCustomerName = lookupCustomers.find((customer) => customer.id === destinationCustomerId)?.name;

                const confirmed = await confirm(
                    `Are you sure you want to copy the selected mix strength${customerMixStrengths.length > 1 ? 's' : ''} from ${this.customer.codeDisplayName} to ${destinationCustomerName}? Any matching existing mix strengths for ${destinationCustomerName} will be overwritten.`,
                    this.appInfo.appTitle
                );
                if (confirmed) {
                    this.appInfo.pleaseWaitShow();
                    const destinationMixStrengths = await this.uow.getCustomerMixStrengthLinks(destinationCustomerId);
                    const affectedMixStrengths: CustomerMixStrengthLink[] = [];
                    const date = new Date();

                    customerMixStrengths.forEach((sourceMixStrength) => {
                        const destinationMixStrength = destinationMixStrengths.find((mixStrength) => mixStrength.mixStrengthId === sourceMixStrength.mixStrengthId);
                        if (destinationMixStrength) {
                            destinationMixStrength.price = sourceMixStrength.price;
                            affectedMixStrengths.push(destinationMixStrength);
                        } else {
                            affectedMixStrengths.push(
                                this.uow.customerMixStrengthLinks.createEntity({
                                    customerId: destinationCustomerId,
                                    mixStrengthId: sourceMixStrength.mixStrengthId,
                                    price: sourceMixStrength.price,
                                    created: date,
                                    createdBy: this.auth.currentUser.userName,
                                })
                            );
                        }
                    });

                    await this.uow.commit(affectedMixStrengths);
                    this.appInfo.pleaseWaitHide();
                    await alert(`Mix strength pricing was copied to ${destinationCustomerName}`, this.appInfo.settings.company);
                }
            }
            modalRef.hide();
        });
    }

    getMixStrengths(includeId?: number) {
        return this.mixStrengths.filter(
            (mixStrength) => includeId === mixStrength.id || !this.customerMixStrengths.some((customerMixStrength) => customerMixStrength.mixStrengthId === mixStrength.id)
        );
    }

    async productPriceOverrideRowDoubleClick(e: any) {
        await this.editProductPriceOverride(e.data);
    }

    async editProductPriceOverride(customerProductPriceOverride?: CustomerProductPriceOverride) {
        const modalRef = this.modalService.show(CustomerProductPriceOverrideDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                customerProductPriceOverride: customerProductPriceOverride,
                customerId: this.customer.id,
                products: this.products,
            },
        });
        modalRef.content?.result.subscribe(async (result) => {
            if (result.success && !this.customerProductPriceOverrides.includes(result.value)) {
                this.customerProductPriceOverrides = [...this.customerProductPriceOverrides, result.value];
            }
            modalRef.hide();
        });
    }

    async deleteProductPriceOverride(customerProductPriceOverrides: CustomerProductPriceOverride[]) {
        if (!customerProductPriceOverrides || customerProductPriceOverrides.length === 0) return;

        const result = await confirm(`Are you sure you want to delete the selected price override${customerProductPriceOverrides.length > 1 ? 's' : ''}?`, this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        customerProductPriceOverrides.forEach((customerProductPriceOverride) => {
            customerProductPriceOverride.deleted = new Date();
            customerProductPriceOverride.deletedBy = this.auth.currentUser.userName;
        });
        this.customerProductPriceOverrides = this.customerProductPriceOverrides.filter((customerProductPriceOverride) => !customerProductPriceOverride.deleted);
        await this.uow.commit(customerProductPriceOverrides);
        this.appInfo.pleaseWaitHide();
    }

    async openInvoice(invoice: Invoice) {
        if (!invoice) return;
        this.router.navigate([`/invoices/${invoice.id}`]);
    }
}
